import { Fragment, useEffect, useRef, useState } from 'react'

import { Widget as TypeformWidget } from '@typeform/embed-react'

import styles from './AppStoreCard.module.scss'
import { AppStoreCardProps } from './AppStoreCard.types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  useAuthState,
  useCausesPortalAnalytics,
  useIntroModalState,
  useIsOrganisationInactive,
  useMarketplaceAnalytics,
  useMutation
} from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import {
  MarketplaceProgrammeVisitedSource,
  OriginCategory
} from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'
import { Button, ButtonText, Icon, Modal, ModalHeader, ModalTitle, Typography } from '@percent/lemonade'
import {
  PrimaryButtonLink,
  ProductOfferType,
  SecondaryButtonLink
} from '@percent/cause-dashboard/app/productOffers/data/data.types'
import AppStoreSuccessModal from './AppStoreSuccessModal/AppStoreSuccessModal'
import { CalendlyContainer } from '@percent/cause-dashboard/common/components'
import goodstackImage from '@percent/cause-dashboard/common/assets/images/goodstack.png'
import { useNativeApply } from '@percent/cause-dashboard/common/components/nativeApply'
import { logger } from '@percent/cause-dashboard/common/utility/logger'
import { GfmInterestConfirmationModal } from '@percent/cause-dashboard/common/components/gfmInterest/GfmInterestConfirmationModal'
import { GFM_INTEREST_EVENTS } from '@percent/cause-dashboard/common/components/gfmInterest/gfmInterestEvents'

export function AppStoreCard({
  offerId,
  name,
  description,
  category,
  type,
  logo,
  primaryButton,
  secondaryButton,
  selectedOfferName,
  selectOffer,
  tracking,
  searchQuery,
  experimentVariant
}: Readonly<AppStoreCardProps>) {
  const { t } = useTranslation()
  const { authState } = useAuthState()
  const { trackProgramVisited: track, trackMarketplaceSearch } = useMarketplaceAnalytics()
  const { openNativeApply } = useNativeApply()
  const isOrganisationInactive = useIsOrganisationInactive()
  const { productOffersService } = useServices()
  const [, { apiFunc: trackProductOfferClick }] = useMutation(
    productOffersService.trackProductOfferClick,
    undefined,
    logger
  )

  const [openTypeformModal, setOpenTypeformModal] = useState<string | undefined>(undefined)
  const [hasSubmittedTypeform, setHasSubmittedTypeform] = useState(false)
  const navigate = useNavigate()
  const { track: trackEvent } = useCausesPortalAnalytics()
  const { recordGfmInterestRegistered } = useIntroModalState()
  const [isInterestConfirmationOpen, setIsInterestConfirmationOpen] = useState(false)
  const hasTrackedGfmView = useRef(false)

  useEffect(() => {
    if (!experimentVariant || hasTrackedGfmView.current) {
      return
    }
    hasTrackedGfmView.current = true
    trackEvent(GFM_INTEREST_EVENTS.shown, { variant: experimentVariant, element: 'product_tile' })
  }, [experimentVariant, trackEvent])

  const hasBuyNowButton =
    primaryButton.linkText === 'productOffers.buyNow' || secondaryButton?.linkText === 'productOffers.buyNow'

  const getPrimaryLink = (button: PrimaryButtonLink) => {
    return button.link ?? ''
  }

  const getSecondaryLink = (button: SecondaryButtonLink) => {
    return button?.link ?? ''
  }

  const getTypeformIFrame = (primaryButton: PrimaryButtonLink, secondaryButton: SecondaryButtonLink) =>
    primaryButton.linkType === 'typeform'
      ? primaryButton.typeformId
      : secondaryButton?.linkType === 'typeform'
      ? secondaryButton.typeformId
      : undefined

  const getCalendlyAvailability = (primaryButton: PrimaryButtonLink, secondaryButton: SecondaryButtonLink) =>
    primaryButton.linkType === 'calendly' || secondaryButton?.linkType === 'calendly'

  const primaryLink = getPrimaryLink(primaryButton)
  const secondaryLink = getSecondaryLink(secondaryButton)
  const isCalendlyCallAvailable = getCalendlyAvailability(primaryButton, secondaryButton)
  const typeformIFrame = getTypeformIFrame(primaryButton, secondaryButton)

  const trackProgramVisited = async (
    link: string,
    linkText: string,
    button: PrimaryButtonLink | SecondaryButtonLink,
    source: MarketplaceProgrammeVisitedSource = MarketplaceProgrammeVisitedSource.Card
  ) => {
    // Inactive orgs fall back to opening the external link instead of the native apply modal, so
    // the click is only a native apply when the modal will actually open.
    const isNativeApply = button?.linkType === 'native_apply' && !isOrganisationInactive
    track({
      source,
      targetUrl: isNativeApply ? '' : link,
      isNativeApply,
      programmeOfferName: name,
      productCategory: category,
      programTracking: tracking,
      metadata: {
        hasBuyNowOption: hasBuyNowButton,
        offerType: type,
        offerName: name,
        originCategory: category,
        buttonText: linkText,
        ...(searchQuery && { searchQuery })
      }
    })
  }

  const handleOnClick = async (link: string, linkText: string, button: SecondaryButtonLink, searchQuery?: string) => {
    if (button?.linkType === 'register_interest') {
      recordGfmInterestRegistered()
      trackEvent(GFM_INTEREST_EVENTS.ctaClicked, {
        variant: experimentVariant,
        element: 'product_tile',
        programmeName: name
      })
      setIsInterestConfirmationOpen(true)
      return
    }

    if (offerId) trackProductOfferClick({ offerId, program: name })

    await trackProgramVisited(link, linkText, button, MarketplaceProgrammeVisitedSource.Card)
    if (searchQuery) await trackMarketplaceSearch({ searchQuery })

    if (button?.linkType === 'native_apply') {
      if (isOrganisationInactive) {
        window.open(link, '_blank')
        return
      }
      openNativeApply({
        partnerKey: button.partnerKey,
        modalContext: button.modalContext,
        tracking: {
          programmeOfferName: name,
          source: MarketplaceProgrammeVisitedSource.Card,
          originCategory: OriginCategory.AppStoreCarousel
        }
      })
      return
    }

    if (typeformIFrame) {
      setOpenTypeformModal(typeformIFrame)
    } else if (button?.linkType === 'external') {
      window.open(link, '_blank')
    } else if (button?.linkType === 'internal') {
      navigate(link)
      window.scrollTo(0, 0)
    } else if (isCalendlyCallAvailable) {
      selectOffer(name)
    }
  }

  const handleCloseTypeformModal = () => {
    setOpenTypeformModal(undefined)
    setHasSubmittedTypeform(false)
  }

  return (
    <>
      <div className={styles.appStoreCardContainer}>
        <div className={styles.cardDetails}>
          <div className={styles.cardHeader}>
            <div className={styles.cardLogoWrapper}>
              {type === ProductOfferType.PercentService && (
                <img src={goodstackImage} alt={name} className={styles.image} />
              )}
              {logo && <img src={logo} alt={name} className={styles.image} />}
            </div>
          </div>
          <Typography variant="h4" color="secondary" className={styles.tileName}>
            {name}
          </Typography>
          {!!description && (
            <Typography data-testid="tile-description" className={styles.tileDescription}>
              {description.split('\n').map((line, index) => (
                <Fragment key={index}>
                  {index > 0 && <br />}
                  <span>{line}</span>
                </Fragment>
              ))}
            </Typography>
          )}
        </div>
        <div className={styles.cardCta}>
          {secondaryButton && (
            <ButtonText
              onClick={async () => {
                await handleOnClick(secondaryLink, t(secondaryButton.linkText), secondaryButton, searchQuery)
              }}
              data-testid={'offer_' + name}
              size={'medium'}
            >
              {t(secondaryButton.linkText)}
            </ButtonText>
          )}
          <Button
            onClick={async () => {
              await handleOnClick(primaryLink, t(primaryButton.linkText), primaryButton, searchQuery)
            }}
            data-testid={'offer_' + name}
            variant={'secondary'}
            size={'medium'}
            stretch
          >
            {t(primaryButton.linkText)}
          </Button>
        </div>
      </div>
      {(primaryButton.linkType === 'register_interest' || secondaryButton?.linkType === 'register_interest') && (
        <GfmInterestConfirmationModal
          open={isInterestConfirmationOpen}
          onClose={() => setIsInterestConfirmationOpen(false)}
          isGoodstack={experimentVariant === 'goodstack_supporters' || experimentVariant === 'goodstack_tools'}
        />
      )}
      {hasSubmittedTypeform && (
        <AppStoreSuccessModal
          trackingData={{
            offerName: name,
            categoryName: category,
            hasBuyNowOption: hasBuyNowButton
          }}
          offerType={type ?? ProductOfferType.PercentService}
          open={hasSubmittedTypeform}
          onClose={handleCloseTypeformModal}
        />
      )}

      {isCalendlyCallAvailable && name === selectedOfferName && (
        <CalendlyContainer
          url={primaryButton.linkType === 'calendly' ? primaryLink : secondaryLink}
          selectItem={selectOffer}
          tracking={{ source: MarketplaceProgrammeVisitedSource.Card, programmeName: name }}
        />
      )}

      {openTypeformModal && !hasSubmittedTypeform && (
        <Modal width={800} open={Boolean(openTypeformModal)} onClose={handleCloseTypeformModal}>
          <ModalHeader>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexGrow: 1,
                padding: 4,
                width: '100%'
              }}
            >
              <ModalTitle>{name}</ModalTitle>
              <button
                style={{
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  marginLeft: 'auto'
                }}
                onClick={handleCloseTypeformModal}
                data-testid={'typeform-dialog'}
              >
                <Icon name="close" size={4} color="gray600" />
              </button>
            </div>
          </ModalHeader>
          <TypeformWidget
            id={openTypeformModal}
            data-testid={'typeform-widget'}
            hidden={{
              organisation_id: authState.claimOrganisation?.id ?? 'unknown',
              email: authState.user?.email ?? 'unknown',
              account_id: authState.user?.id ?? 'unknown'
            }}
            className={styles.typeformWidget}
            onSubmit={() => setHasSubmittedTypeform(true)}
          />
        </Modal>
      )}
    </>
  )
}
